/**
 * English: Normalize task_runs.mode for inbox filters and labels (legacy chat/insights → ask).
 * Thai: ปรับ mode จาก task_runs สำหรับตัวกรองและป้ายบนหน้าประวัติงาน
 */
import type { TaskRun } from '../types';

/** Modes accepted from the API before display normalization. */
export const TASK_INBOX_MODES = [
  'ask',
  'control',
  'employee',
  'master',
  'agent',
  'chat',
  'insight',
  'insights',
] as const;

/**
 * Mode filter pills on /tasks (2 lanes + “ทุกโหมด”).
 * Device-direct (`employee`) rows are classified as control — no separate pill.
 */
export const TASK_INBOX_FILTER_MODES = ['ask', 'control'] as const;

const CONTROL_HISTORY_HINT =
  /confirm_execute|โหมดควบคุม|automanual|manual mode|intent["']?\s*:\s*["']control|message_type["']?\s*:\s*["']confirm_/iu;

export function normalizeTaskRunMode(mode: string | null | undefined): string {
  const m = (mode ?? '').trim().toLowerCase();
  if (!m) return '';
  if (m === 'ask' || m === 'insight' || m === 'insights' || m === 'chat') return 'ask';
  return m;
}

/** Classify a row for inbox filters — recovers legacy control rows stored as insight/employee. */
export function inboxModeForTaskRun(
  run: Pick<TaskRun, 'mode' | 'prompt' | 'summary'>,
): string {
  const raw = (run.mode ?? '').trim().toLowerCase();
  const stored = normalizeTaskRunMode(run.mode);

  if (stored === 'control') return 'control';
  if (stored === 'employee') return 'control';

  if (raw === 'insight' || raw === 'insights' || raw === 'ask' || raw === 'chat') {
    const hay = `${run.prompt ?? ''} ${run.summary ?? ''}`;
    if (CONTROL_HISTORY_HINT.test(hay)) return 'control';
    return stored;
  }

  if (stored) return stored;
  return stored;
}

export function isTaskRunVisibleInInbox(mode: string | null | undefined): boolean {
  const normalized = normalizeTaskRunMode(mode);
  if (!normalized) return false;
  return (TASK_INBOX_MODES as readonly string[]).includes(normalized)
    || (TASK_INBOX_MODES as readonly string[]).includes((mode ?? '').trim().toLowerCase());
}

export function taskRunMatchesModeFilter(
  run: Pick<TaskRun, 'mode' | 'prompt' | 'summary'>,
  modeFilter: string,
): boolean {
  return inboxModeForTaskRun(run) === modeFilter;
}

export function modeFilterCount(
  runs: Pick<TaskRun, 'mode' | 'prompt' | 'summary'>[],
  modeFilter: string,
): number {
  return runs.filter((run) => taskRunMatchesModeFilter(run, modeFilter)).length;
}
